// Convert a captured HTML snippet into a Breakdance clipboard payload that
// pastes natively into the Breakdance editor.
//
// Clipboard shape (reverse-engineered from real Breakdance copies):
//
//   {
//     "element": {
//       "id": <number>,
//       "data": { "type": "EssentialElements\\X", "properties": { … } | null },
//       "children": [ /* same shape minus the outer "element" wrapper */ ],
//       "_parentId": <number>
//     }
//   }
//
// IDs are integers; pastes appear to re-id on the receiving side so we
// just use sequential numbers starting at 100. `_parentId: 1` is the
// document root.
//
// Smart-mapping rules: simple structural tags map to native Breakdance
// elements (Heading / Text / RichText / Button / TextLink). Anything we
// can't map confidently falls through to a CodeBlock — Breakdance's
// CodeBlock element accepts arbitrary HTML in its `php_code` field and
// renders it verbatim.

export type BDElement = {
  id: number;
  data: {
    type: string;
    properties: Record<string, unknown> | null;
  };
  children: BDElement[];
  _parentId: number;
};

export const ROOT_PARENT_ID = 1;

export function toBreakdanceClipboard(snippet: string): string {
  const root = parseSnippet(snippet);
  const element = root
    ? mapNode(root, ROOT_PARENT_ID, idGen())
    : codeBlockElement(snippet, ROOT_PARENT_ID, idGen());
  return JSON.stringify({ element });
}

export function idGen(): () => number {
  let n = 100;
  return () => n++;
}

function parseSnippet(html: string): Element | null {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.firstElementChild;
  } catch {
    return null;
  }
}

// ---------- dispatcher ----------

function mapNode(el: Element, parentId: number, nextId: () => number): BDElement {
  const tag = el.tagName.toLowerCase();
  const type = el.getAttribute("type")?.toLowerCase() ?? "";

  if (/^h[1-6]$/.test(tag) && onlyPlainText(el)) {
    return headingElement(el, tag as "h1" | "h2" | "h3" | "h4" | "h5" | "h6", parentId, nextId);
  }
  if (tag === "p" && onlyPlainText(el)) {
    return textElement(el.textContent ?? "", parentId, nextId);
  }
  if (tag === "button" || (tag === "input" && (type === "submit" || type === "button"))) {
    return buttonElement(el, parentId, nextId);
  }
  if (tag === "a") {
    const href = el.getAttribute("href") ?? "";
    const cls = el.getAttribute("class") ?? "";
    if (href && /\b(btn|button)\b/i.test(cls)) {
      return buttonElement(el, parentId, nextId);
    }
    if (href && onlyPlainText(el)) {
      return textLinkElement(el.textContent ?? el.getAttribute("href") ?? "", href, parentId, nextId);
    }
    // anchor with nested structure → fall through to code block
    return codeBlockElement(el.outerHTML, parentId, nextId);
  }
  // Lists and paragraph-like blocks with inline formatting render best as
  // RichText, which Breakdance treats as a typography-aware HTML editor.
  if (tag === "ul" || tag === "ol" || (tag === "p" && !onlyPlainText(el))) {
    return richTextElement(el.outerHTML, parentId, nextId);
  }
  // Everything else (section, div, nav, article, header, footer, img,
  // form, …) drops into a CodeBlock so the raw markup renders.
  return codeBlockElement(el.outerHTML, parentId, nextId);
}

// ---------- element builders ----------

function headingElement(
  el: Element,
  tag: "h1" | "h2" | "h3" | "h4" | "h5" | "h6",
  parentId: number,
  nextId: () => number,
): BDElement {
  return {
    id: nextId(),
    data: {
      type: "EssentialElements\\Heading",
      properties: {
        content: {
          content: {
            text: (el.textContent ?? "").trim(),
            tags: tag,
          },
        },
      },
    },
    children: [],
    _parentId: parentId,
  };
}

function textElement(text: string, parentId: number, nextId: () => number): BDElement {
  return {
    id: nextId(),
    data: {
      type: "EssentialElements\\Text",
      properties: {
        content: {
          content: {
            text: text.trim(),
          },
        },
      },
    },
    children: [],
    _parentId: parentId,
  };
}

function richTextElement(html: string, parentId: number, nextId: () => number): BDElement {
  return {
    id: nextId(),
    data: {
      type: "EssentialElements\\RichText",
      properties: {
        content: {
          content: {
            text: html,
          },
        },
      },
    },
    children: [],
    _parentId: parentId,
  };
}

function buttonElement(el: Element, parentId: number, nextId: () => number): BDElement {
  const text = (el.textContent ?? "").trim() || "Button";
  const href = el.getAttribute("href") ?? "";
  const link = href ? { type: "url", url: href } : undefined;
  return {
    id: nextId(),
    data: {
      type: "EssentialElements\\Button",
      properties: {
        content: {
          content: {
            text,
            ...(link ? { link } : {}),
          },
        },
        design: { button: { style: "custom" } },
      },
    },
    children: [],
    _parentId: parentId,
  };
}

function textLinkElement(
  text: string,
  href: string,
  parentId: number,
  nextId: () => number,
): BDElement {
  return {
    id: nextId(),
    data: {
      type: "EssentialElements\\TextLink",
      properties: {
        content: {
          content: {
            link: { type: "url", url: href },
            text: text.trim() || href,
          },
        },
      },
    },
    children: [],
    _parentId: parentId,
  };
}

function codeBlockElement(html: string, parentId: number, nextId: () => number): BDElement {
  // CodeBlock surfaces a php_code field that renders verbatim — anything
  // outside <?php …?> is emitted as literal HTML, which is exactly what
  // we want for captured snippets we can't smart-map.
  return {
    id: nextId(),
    data: {
      type: "EssentialElements\\CodeBlock",
      properties: {
        content: {
          content: {
            php_code: html,
          },
        },
      },
    },
    children: [],
    _parentId: parentId,
  };
}

// ---------- helpers ----------

// An element has "only plain text" when every descendant is a Text node —
// no child <strong>, <span>, <a>, etc. Used to decide between native
// Heading/Text/TextLink (plain) and RichText/CodeBlock (formatted).
function onlyPlainText(el: Element): boolean {
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === 1) return false; // element node
  }
  return true;
}
